/**
 * Unified acknowledge state abstraction.
 *
 * The legacy event system uses numeric values:
 *   1 = not acknowledged ("undefined")
 *   2 = acknowledged
 *   4 = timeout
 *
 * The S2S event system uses string values:
 *   "undefined"    = not acknowledged
 *   "acknowledged" = acknowledged
 *   "timeout"      = timeout
 *
 * This module normalizes both into a single enum so the UI can treat
 * them interchangeably.
 */

export type NormalizedAckState =
  | 'unacknowledged'
  | 'acknowledged'
  | 'timeout'
  | 'none';

// --- Legacy (numeric) mapping ---

const LEGACY_ACK_UNACKNOWLEDGED = 1;
const LEGACY_ACK_ACKNOWLEDGED = 2;
const LEGACY_ACK_TIMEOUT = 4;

export const fromLegacyAckState = (
  value: number | null | undefined,
): NormalizedAckState => {
  switch (value) {
    case LEGACY_ACK_UNACKNOWLEDGED:
      return 'unacknowledged';
    case LEGACY_ACK_ACKNOWLEDGED:
      return 'acknowledged';
    case LEGACY_ACK_TIMEOUT:
      return 'timeout';
    default:
      return 'none';
  }
};

// --- S2S (string) mapping ---

export const fromS2SAckState = (
  value: string | null | undefined,
): NormalizedAckState => {
  switch (value) {
    case 'undefined':
      return 'unacknowledged';
    case 'acknowledged':
      return 'acknowledged';
    case 'timeout':
      return 'timeout';
    default:
      return 'none';
  }
};

// --- Helpers for UI consumption ---

export const isAcknowledged = (state: NormalizedAckState): boolean =>
  state === 'acknowledged';

export const requiresAcknowledgement = (state: NormalizedAckState): boolean =>
  state === 'unacknowledged';

export const isTimedOut = (state: NormalizedAckState): boolean =>
  state === 'timeout';
